import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  default as ReactSelect,
  MultiValue,
  MultiValueProps,
  StylesConfig,
  components,
} from 'react-select';
import type { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import styled from 'styled-components';
import type * as CSS from 'csstype';
import { devlog } from 'utilities';
import { Label } from 'shared/Typhography/InputStyled';

import { breakpoints } from '../../../theme';

export interface Option<Value = string | number | boolean, Label = string | React.ReactNode> {
  readonly value: Value;
  readonly label: Label;
  readonly searchValue?: string; // Added to make complex labels easier to search.
}

export interface SelectMultiProps {
  className?: string;
  options: Option[] | undefined;
  name?: string;
  width?: string;
  value?: MultiValue<Option>;
  placeholder?: string;
  isDisabled?: boolean;
  maxMenuHeight?: number;
  style?: any;
  defaultValue?: MultiValue<Option>;
  includeCustomSearch?: boolean;
  label?: string;
  /** Id for label's htmlFor attribute. */
  id?: string;
  filterOption?: ((option: FilterOptionOption<Option>, inputValue: string) => boolean) | null;
  onChange: (option: MultiValue<Option>) => void;
  onKeyPress?: (event?: any) => void;
  isClearable?: boolean;
  isSearchable?: boolean;
  /** Allows reordering selected values by dragging pills. Defaults to true. */
  isSortable?: boolean;
}

export interface SelectContainerProps {
  width?: string;
}

const SelectContainer = styled.div<SelectContainerProps>`
  width: ${({ width }) => (width ? width : '300px')};
  @media (${breakpoints.lg}) {
    width: 100%;
  }
`;

const style: StylesConfig = {
  control: (base) => ({
    ...base,
    '&:hover': { boxShadow: '0px 0px 10px #c4c4c4', transition: '0.3s' },
    boxShadow: '0px 0px 3px #c4c4c4',
    border: 0,
    borderRadius: '35px',
    textAlign: 'center',
    width: '100%',
    minHeight: '50px',
  }),
  option: (base, { isSelected, isFocused }) => {
    let backgroundColor: CSS.Property.BackgroundColor | undefined;

    // Show proper hover and selected styles.
    if (isSelected && isFocused) {
      backgroundColor = isSelected && isFocused ? '#585CEA77' : backgroundColor;
    } else if (isFocused) {
      backgroundColor = isFocused ? '#00000011' : backgroundColor;
    } else if (isSelected) {
      backgroundColor = isSelected ? '#585CEA' : undefined;
    }

    return { ...base, backgroundColor };
  },
};

const reorderMultiValue = <T extends Option>(
  values: readonly T[],
  fromIndex: number,
  toIndex: number,
): T[] => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return [...values];
  }

  const reorderedValues = [...values];
  const [movedValue] = reorderedValues.splice(fromIndex, 1);
  reorderedValues.splice(toIndex, 0, movedValue);

  return reorderedValues;
};

interface SortableDragContextValue {
  dragIndexRef: React.MutableRefObject<number | null>;
  draggingIndex: number | null;
  setDraggingIndex: React.Dispatch<React.SetStateAction<number | null>>;
}

const SortableDragContext = createContext<SortableDragContextValue | null>(null);

const SortableMultiValue = (multiValueProps: MultiValueProps<Option, true>) => {
  const sortableDragContext = useContext(SortableDragContext);
  const [isDragOver, setIsDragOver] = useState(false);
  const { selectProps, data, index: currentIndex, innerProps } = multiValueProps;

  if (!sortableDragContext) {
    return <components.MultiValue {...multiValueProps} />;
  }

  const { dragIndexRef, draggingIndex, setDraggingIndex } = sortableDragContext;
  const selectedValues = (selectProps.value ?? []) as Option[];
  const isBeingDragged = draggingIndex === currentIndex;

  const reorderValues = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }

    selectProps.onChange(reorderMultiValue(selectedValues, fromIndex, toIndex), {
      action: 'select-option',
      option: data,
    });
  };

  const isRemoveTarget = (target: EventTarget | null) =>
    (target as HTMLElement | null)?.closest('[class*="multi-value__remove"]') !== null;

  return (
    <components.MultiValue
      {...multiValueProps}
      innerProps={{
        ...innerProps,
        onMouseDown: (event) => {
          innerProps?.onMouseDown?.(event);

          if (selectProps.isDisabled || isRemoveTarget(event.target)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          dragIndexRef.current = currentIndex;
          setDraggingIndex(currentIndex);
        },
        onMouseEnter: (event) => {
          innerProps?.onMouseEnter?.(event);

          if (dragIndexRef.current === null || dragIndexRef.current === currentIndex) {
            return;
          }

          setIsDragOver(true);
        },
        onMouseLeave: (event) => {
          innerProps?.onMouseLeave?.(event);
          setIsDragOver(false);
        },
        onMouseUp: (event) => {
          innerProps?.onMouseUp?.(event);

          if (dragIndexRef.current === null) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          if (dragIndexRef.current !== currentIndex) {
            reorderValues(dragIndexRef.current, currentIndex);
          }

          dragIndexRef.current = null;
          setDraggingIndex(null);
          setIsDragOver(false);
        },
        style: {
          ...(innerProps?.style as React.CSSProperties),
          opacity: isBeingDragged ? 0.5 : 1,
          boxShadow: isDragOver ? '0 0 0 2px #585CEA' : undefined,
          cursor: selectProps.isDisabled ? 'default' : 'grab',
          userSelect: isBeingDragged ? 'none' : undefined,
        },
      }}
    />
  );
};

const SortableMultiValueComponent = (multiValueProps: MultiValueProps<Option, true>) => (
  <SortableMultiValue {...multiValueProps} />
);

/**
 * @todo: Trigger Select input on label click.
 */
export const SelectMulti = (props: SelectMultiProps) => {
  const { isSortable = true, ...selectProps } = props;
  const dragIndexRef = useRef<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (draggingIndex === null) {
      return;
    }

    const handleMouseUp = () => {
      dragIndexRef.current = null;
      setDraggingIndex(null);
    };

    document.addEventListener('mouseup', handleMouseUp);

    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [draggingIndex]);

  const sortableDragContextValue = useMemo(
    () => ({
      dragIndexRef,
      draggingIndex,
      setDraggingIndex,
    }),
    [draggingIndex],
  );

  if (selectProps.label && !selectProps.id) {
    console.warn('Id must also be defined when label is present');
  }

  const selectComponents = useMemo(
    () => (isSortable ? { MultiValue: SortableMultiValueComponent } : undefined),
    [isSortable],
  );

  const select = (
    <ReactSelect<Option, true>
      {...selectProps}
      isMulti
      components={selectComponents as any}
      styles={
        style as any /* any because this ReactSelect wrapper component's props weren't typed properly from the beginning */
      }
      filterOption={
        selectProps.includeCustomSearch ? selectProps.filterOption || customSearchFilter : undefined
      }
    />
  );

  return (
    <SelectContainer
      className={selectProps.className}
      width={selectProps?.width}
      style={{ position: 'relative', ...selectProps.style }}
    >
      {selectProps.label && <Label htmlFor={selectProps.id}>{selectProps.label}</Label>}
      {isSortable ? (
        <SortableDragContext.Provider value={sortableDragContextValue}>
          {select}
        </SortableDragContext.Provider>
      ) : (
        select
      )}
    </SelectContainer>
  );
};

/**
 * Because artists label contains profile image it messes up search functionality,
 * we added this simple custom search to bypass it, it is put here because it may be
 * reused in future for similar purposes.
 *
 * Note: For this function to work you need to add "searchValue" field to your Select's options.
 *
 * @param option Option to compare (passed in by react-select).
 * @param inputValue  Value of search input.
 */
function customSearchFilter(option: FilterOptionOption<Option>, inputValue: string): boolean {
  try {
    // Replace regex special characters with literals to prevent malformed RegExp creation
    // List of characters that needs escaping can be found e.g. here:
    // https://www3.ntu.edu.sg/home/ehchua/programming/howto/Regexe.html#zz-2.2
    inputValue = inputValue.replaceAll(/([.[\]^$+*?{}()|\\])/g, '\\$1');
    const regex = new RegExp(inputValue, 'i');
    return option.data.searchValue?.match(regex) ? true : false;
  } catch (e) {
    devlog('error', 'Error while searching in Select: ', e);
  }

  return false;
}
